import React, { FC, useRef, MouseEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { isBloggerBusiness } from '../../../../utils/accessService';
import { buildAvatarImageSource } from '../../../../utils/buildAvatarImageSource';
import { cropPublicId } from '../../../../utils/cropPublicId';
import { formatDistanceToNow } from '../../../../utils/format/formatDistanceToNow';
import { formatText } from '../../../../utils/format/formatText';
import { TransitionType, makeTransition } from '../../../../utils/styles/makeTransition';
import { useTranslation } from '../../../../utils/useTranslation';
import { Skeleton } from '../../../feedback/Skeleton';
import { IconFollow } from '../../../icon/IconFollow';

import { IconLink } from '../../../icon/IconLink';
import { IconUnfollowChecked } from '../../../icon/IconUnfollowChecked';
import { IconButton } from '../../../input/IconButton';
import { Box } from '../../../layout/Box';
import { DataTestId } from '../../../layout/DataTestIdWrapper';
import { Flex } from '../../../layout/Flex';
import { Avatar } from '../../../media/Avatar';
import { Typography } from '../../../typography/Typography';
import { PostUI } from '../../types';
import { PostButton } from '../PostButton/PostButton';
import {
    DEFAULT_DESCRIPTION_HEIGHT,
    DESCRIPTION_INDENTION,
    PRODUCT_BUTTON_TRANSITION_TIMING,
    TRANSITION_TIMING_KOEF,
} from './constants';
import { ContentExpander, Description, Footer, Text } from './styles';

const getDescriptionMaxHeight = (postElem: HTMLDivElement | null) =>
    (postElem?.offsetHeight || 0) - DESCRIPTION_INDENTION;

const getFirstParagraph = (str: string) => `${str.split('\n')[0]}...`;

const getTransitionTiming = (descriptionHeight: number) =>
    Math.log(descriptionHeight) * TRANSITION_TIMING_KOEF;

interface AnimateFooterProps {
    isExpanded: boolean;
    postElement: HTMLDivElement | null;
    post: PostUI;
    isProductExpanded: boolean;
    onSubscribe: (event: MouseEvent<HTMLElement>) => void;
    onExpand: (event: MouseEvent<HTMLElement>) => void;
    onClickOutClick: (event: MouseEvent<HTMLElement>) => void;
    onProductButtonClick: VoidFunction;
}

export const AnimateFooter: FC<AnimateFooterProps> = ({
    isExpanded,
    postElement,
    post,
    onSubscribe,
    onExpand,
    onClickOutClick,
    isProductExpanded,
    onProductButtonClick,
}) => {
    const [descriptionHeight, setDescriptionHeight] = useState(0);
    const clickOut = (post.clickouts ?? []).find((c) => c.target === 'button' && c.url);
    const t = useTranslation();
    const expandedTransitionTiming = getTransitionTiming(descriptionHeight);
    const footerTransitionTimingRef = useRef(PRODUCT_BUTTON_TRANSITION_TIMING);

    if (isExpanded) {
        footerTransitionTimingRef.current = expandedTransitionTiming;
    }

    if (isProductExpanded) {
        footerTransitionTimingRef.current = PRODUCT_BUTTON_TRANSITION_TIMING;
    }

    const businessName = isBloggerBusiness(post.business?.type)
        ? post.business?.displayName
        : cropPublicId(post.business?.publicId || '');

    return (
        <Footer>
            <ContentExpander
                isExpanded={isExpanded}
                isProductExpanded={isProductExpanded}
                descriptionHeight={descriptionHeight}
                transition={makeTransition({
                    ms: footerTransitionTimingRef.current,
                    styles: ['padding-bottom', 'transform'],
                    type:
                        footerTransitionTimingRef.current === PRODUCT_BUTTON_TRANSITION_TIMING
                            ? TransitionType.EASE_OUT
                            : TransitionType.LINEAR,
                })}
            >
                <Box overflow="hidden" width="100%">
                    <Flex
                        pointerEvents="auto"
                        width="100%"
                        direction="row"
                        justifyContent="space-between"
                        alignItems="flex-end"
                        mb="12px"
                        mt="4px"
                    >
                        {post.business ? (
                            <Flex direction="row" alignItems="center" width="100%">
                                <Flex
                                    as={Link}
                                    to={post.business.url}
                                    direction="row"
                                    alignItems="center"
                                    spacing={1}
                                    textDecoration="none"
                                    dataTestId={DataTestId.BUSINESS_LOGIN}
                                >
                                    <Avatar
                                        width={34}
                                        title={post.business.displayName}
                                        src={post.business.avatarUrl}
                                        border="1px solid var(--divider)"
                                    />
                                    <Text weight="bold">{businessName}</Text>
                                </Flex>
                                <Box ml={1}>
                                    <IconButton
                                        onClick={onSubscribe}
                                        marginBottom={2}
                                        dataTestId={DataTestId.SUBSCRIBE_ICON}
                                    >
                                        {post.business?.subscribed ? (
                                            <IconUnfollowChecked
                                                fontSize={24}
                                                color="#FFFFFF"
                                                dataTestId={DataTestId.SUBSCRIBED}
                                            />
                                        ) : (
                                            <IconFollow
                                                fontSize={24}
                                                color="#FFFFFF"
                                                dataTestId={DataTestId.UNSUBSCRIBED}
                                            />
                                        )}
                                    </IconButton>
                                </Box>
                            </Flex>
                        ) : null}
                        {post.ownersData ? (
                            <Flex direction="row" alignItems="center" width="100%">
                                <Flex
                                    direction="row"
                                    alignItems="center"
                                    spacing={1}
                                    textDecoration="none"
                                    dataTestId={DataTestId.USER_LOGIN}
                                >
                                    <Flex position="relative">
                                        <Avatar
                                            width={24}
                                            title={post.ownersData.author.displayName}
                                            src={buildAvatarImageSource(
                                                post.ownersData.author.avatarUrl,
                                            )}
                                        />
                                        <Box
                                            position="absolute"
                                            right="-3px"
                                            top="-3px"
                                            borderRadius="50%"
                                            backgroundColor="#FFF"
                                            border="solid 2px transparent"
                                        >
                                            <IconLink size={10} />
                                        </Box>
                                    </Flex>
                                    <Flex>
                                        <Typography variant="caption" weight="bold" color="#FFF">
                                            {post.ownersData.shop.displayUrl}
                                        </Typography>
                                        <Typography variant="caption2" color="#FFF">
                                            {post.ownersData.author.displayName}
                                        </Typography>
                                    </Flex>
                                </Flex>
                            </Flex>
                        ) : null}
                    </Flex>
                </Box>

                <Box
                    onClick={onExpand}
                    cursor="pointer"
                    dataTestId={DataTestId.ANIMATE_FOOTER_DESCRIPTION}
                >
                    <Description
                        transition={makeTransition({
                            styles: ['opacity', 'width'],
                            ms: isProductExpanded
                                ? PRODUCT_BUTTON_TRANSITION_TIMING
                                : expandedTransitionTiming,
                        })}
                        isExpanded={isExpanded}
                        isProductExpanded={isProductExpanded}
                        type="short"
                    >
                        {getFirstParagraph(post.text)}
                    </Description>
                    <Description
                        ref={(node) => setDescriptionHeight(node?.offsetHeight || 0)}
                        transition={makeTransition({
                            styles: ['opacity'],
                            ms: expandedTransitionTiming,
                        })}
                        maxHeight={
                            getDescriptionMaxHeight(postElement) || DEFAULT_DESCRIPTION_HEIGHT
                        }
                        isExpanded={isExpanded}
                        isProductExpanded={isProductExpanded}
                        type="normal"
                    >
                        {formatText(post.text)}
                        <Text as="span" opacity={0.75} mt={2}>
                            {formatDistanceToNow(post.createdAt)}
                            {post.likes > 10 && (
                                <Box display="inline" as="span" ml={2}>
                                    {t('Понравилось: ')}
                                    {post.likes}
                                </Box>
                            )}
                        </Text>
                    </Description>
                </Box>
            </ContentExpander>
            <PostButton
                onClickOutClick={onClickOutClick}
                isExpanded={isExpanded}
                clickOut={clickOut}
                transitionTiming={footerTransitionTimingRef.current}
                products={post.products}
                businessType={post.business?.type}
                isProductExpanded={isProductExpanded}
                onProductButtonClick={onProductButtonClick}
            />
        </Footer>
    );
};

export const AnimateFooterSkeleton = () => (
    <Flex p={1} direction="row" justifyContent="space-between" alignItems="center" width="100%">
        <Flex>
            <Flex justifyContent="space-between" direction="row" alignItems="center">
                <Skeleton variant="circular" width={34} height={34} />
                <Skeleton variant="text" width={150} height={18} ml={1} />
            </Flex>
            <Skeleton variant="text" width={260} height={18} mt={2} />
        </Flex>
        <Flex justifyContent="space-between">
            <Flex position="absolute" bottom="5rem" right="1rem">
                <Skeleton variant="circular" width={34} height={34} mt={1.5} />
                <Skeleton variant="circular" width={34} height={34} mt={1.5} />
                <Skeleton variant="circular" width={34} height={34} mt={1.5} />
                <Skeleton variant="circular" width={34} height={34} mt={1.5} />
            </Flex>
            <Skeleton variant="rect" width="100px" borderRadius="999px" height={34} mt="3rem" />
        </Flex>
    </Flex>
);
